import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { PartnerDealsRealtimeService } from '@features/dashboard/commercial/services/partner-deals-realtime.service';
import { CommercialClientTermResponse } from '@interfaces/partners/responses/commercial-client-term.response';
import { PartnerDealResponse } from '@interfaces/partners/responses/partner-deal.response';
import { PartnerEarningsResponse } from '@interfaces/partners/responses/partner-flat-fee.response';
import { Permissions } from '@guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { Store } from '@ngrx/store';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { RealtimeListRefreshService } from '@services/realtime/realtime-list-refresh.service';
import { selectFirstName } from '@app/state/auth/auth.selectors';
import { AppState } from '@src/app/state';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import {
  buildDealList,
  buildFunnel,
  buildPendingItems,
  buildSummary,
  DealListFilter,
  dealsNeedingTerm,
  isActiveDeal,
  PendingItem,
  toPartnerEarnings,
} from './partner-home-presenter';

const MILLISECONDS_IN_A_DAY = 86_400_000;

/**
 * Início do parceiro (card 3517): a primeira tela que o parceiro vê ao entrar.
 *
 * Layout da opção A do protótipo no acervo (`inicio-do-parceiro-opcoes-de-tela-card-3517`): quatro
 * números no topo, "O que depende de você agora" e o funil por etapa. As regras de contagem e de
 * pendência ficam no `partner-home-presenter.ts`; aqui só se carrega e se liga o template.
 *
 * Dados: o board (`GET partners/deals`, o mesmo de Meus deals, então os números batem) e o termo
 * de cada deal em Consentimento, que é o único jeito de saber se o NDA já foi enviado ou assinado.
 *
 * Card 3707: "Volume em captação" e "Fee estimado" acima dos números, calculados no backend
 * (`GET partners/me/earnings`, permissão `COMMERCIAL_MY_EARNINGS`): valor × flat fee do prazo × percentual
 * do contrato de parceria (ou 20% sem contrato). Sem a permissão os dois cartões não aparecem; se a leitura
 * falhar, eles dizem que não foi possível carregar.
 */
@Component({
  selector: 'app-partner-home',
  standalone: true,
  imports: [CommonModule, SharedModule, RouterLink],
  templateUrl: './partner-home.component.html',
  styleUrls: ['./partner-home.component.scss'],
  // Tempo real (card 3496): relê quando um deal do parceiro muda em outra sessão, como o board.
  providers: [RealtimeListRefreshService, PartnerDealsRealtimeService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerHomeComponent implements OnInit {
  private readonly partnersService = inject(PartnersHttpService);
  private readonly dateFormatter = inject(DateFormatterService);
  private readonly partnerDealsRealtime = inject(PartnerDealsRealtimeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store<AppState>);

  readonly breadcrumbs: MenuItem[] = [{ label: 'Comercial' }, { label: 'Início' }];
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/commercial/home' };

  readonly firstName = toSignal(this.store.select(selectFirstName), { initialValue: null });

  readonly loading = signal(true);
  readonly failed = signal(false);
  private readonly deals = signal<PartnerDealResponse[]>([]);
  private readonly terms = signal<ReadonlyMap<string, CommercialClientTermResponse | null>>(
    new Map()
  );

  readonly pendingItems = computed<PendingItem[]>(() =>
    buildPendingItems(this.deals(), this.terms(), deal => this.reservationDaysLeft(deal))
  );
  readonly summary = computed(() =>
    buildSummary(this.deals(), this.pendingItems(), deal => this.reservationDaysLeft(deal))
  );
  readonly funnel = computed(() => buildFunnel(this.deals()));

  /** Quem pode ver volume e fee estimado (card 3707): a permissão própria do Início. */
  readonly canSeeEarnings = toSignal(
    inject(PermissionsService).hasPermission(Permissions.Commercial.MY_EARNINGS),
    { initialValue: false }
  );

  /** Volume e fee estimado como o backend calculou (card 3707); nulo enquanto carrega ou se falhar. */
  readonly earningsResponse = signal<PartnerEarningsResponse | null>(null);
  readonly earningsFailed = signal(false);
  readonly earnings = computed(() => toPartnerEarnings(this.earningsResponse()));

  /**
   * Linha de baixo do fee: quantos deals ficaram fora por falta de prazo. O percentual do parceiro
   * ("20% do flat fee…") não aparece desde o card 3745, pedido do Breno.
   */
  readonly feeLabel = computed(() => {
    if (this.earningsFailed()) return 'Não foi possível carregar o fee agora. Recarregue a página.';
    if (!this.earningsResponse()) return 'Calculando…';
    const withoutTerm = this.earnings().dealsWithoutTerm;
    if (!withoutTerm) return 'estimativa pelos deals ativos';
    return `${withoutTerm} ${withoutTerm === 1 ? 'deal sem prazo fica' : 'deals sem prazo ficam'} de fora`;
  });
  readonly hasDeals = computed(() => this.deals().length > 0);

  /** Volume e fee aparecem na faixa do topo (card 3745) só com deals carregados, como antes. */
  readonly showEarnings = computed(
    () => this.canSeeEarnings() && !this.loading() && !this.failed() && this.hasDeals()
  );

  /** Filtro da lista "Seus deals" (card 3558). Abre em Todos: o pedido foi ver também os rejeitados. */
  readonly dealFilter = signal<DealListFilter>('all');
  readonly dealFilters: ReadonlyArray<{ value: DealListFilter; label: string }> = [
    { value: 'all', label: 'Todos' },
    { value: 'active', label: 'Ativos' },
    { value: 'rejected', label: 'Rejeitados' },
  ];
  readonly dealRows = computed(() => buildDealList(this.deals(), this.dealFilter()));

  /** Linha de baixo de "Deals ativos": "4 no total, 2 rejeitados". */
  readonly totalLabel = computed(() => {
    const { totalCount, closedCount } = this.summary();
    if (!closedCount) return `${totalCount} no total`;
    return `${totalCount} no total, ${closedCount} ${closedCount === 1 ? 'rejeitado' : 'rejeitados'}`;
  });

  private loadSubscription?: Subscription;
  private loadGeneration = 0;

  ngOnInit(): void {
    this.load();

    // O Início é só do parceiro: o servidor já põe a conexão no grupo `partner:{id}`.
    this.partnerDealsRealtime.setInternalView(false);
    this.partnerDealsRealtime.refresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(true));
  }

  retry(): void {
    this.load();
  }

  /** Quantos deals cada filtro mostra (o número ao lado do rótulo). */
  dealCount(filter: DealListFilter): number {
    const deals = this.deals();
    if (filter === 'active') return deals.filter(isActiveDeal).length;
    if (filter === 'rejected') return deals.filter(deal => !isActiveDeal(deal)).length;
    return deals.length;
  }

  greeting(): string {
    const name = this.firstName()?.trim();
    return name ? `Olá, ${name}` : 'Olá';
  }

  /**
   * Carrega o board e os termos dos deals em Consentimento. Uma leitura nova cancela a anterior.
   * @param silent releitura do tempo real: sem skeleton e, se falhar, a tela fica como estava.
   */
  private load(silent = false): void {
    const showsLoading = !silent || this.loading();
    const generation = ++this.loadGeneration;
    this.loadSubscription?.unsubscribe();

    if (showsLoading) {
      this.loading.set(true);
      this.failed.set(false);
    }

    this.loadSubscription = this.partnersService
      .getMyDeals()
      .pipe(
        switchMap(deals => {
          const list = deals ?? [];
          const withTerm = dealsNeedingTerm(list);
          if (!withTerm.length) return of({ deals: list, terms: new Map() });

          // Termo que não carregou conta como "não enviado": a pendência aparece e a tela do
          // deal, ao abrir, mostra o estado verdadeiro. Esconder a pendência seria pior.
          return forkJoin(
            withTerm.map(deal =>
              this.partnersService.getDealTerm(deal.id).pipe(
                catchError(() => of(null)),
                map(term => [deal.id, term] as const)
              )
            )
          ).pipe(map(entries => ({ deals: list, terms: new Map(entries) })));
        }),
        finalize(() => {
          if (generation !== this.loadGeneration) return;
          this.loading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ deals, terms }) => {
          this.deals.set(deals);
          this.terms.set(terms);
          this.failed.set(false);
          this.loadEarnings();
        },
        error: () => {
          if (showsLoading) this.failed.set(true);
        },
      });
  }

  /**
   * Volume e fee estimado (card 3707), calculados no backend. Relido junto com o board (inclusive pelo tempo
   * real), para os cartões acompanharem os deals. Sem a permissão não chama; falha não derruba o Início, só
   * os dois cartões dizem que não carregaram.
   */
  private loadEarnings(): void {
    if (!this.canSeeEarnings()) return;
    this.partnersService
      .getMyEarnings()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: earnings => {
          this.earningsResponse.set(earnings);
          this.earningsFailed.set(!earnings);
        },
        error: () => this.earningsFailed.set(true),
      });
  }

  /** Dias restantes da reserva do CNPJ; nulo quando ela já é definitiva (mesma conta do board). */
  private reservationDaysLeft(deal: PartnerDealResponse): number | null {
    const expiresAt = this.dateFormatter.parseDateTruncateTime(deal.reservationExpiresAt);
    if (!expiresAt) return null;

    const today = this.dateFormatter.parseDateTruncateTime(this.dateFormatter.getTodayDate())!;
    return Math.ceil((expiresAt.getTime() - today.getTime()) / MILLISECONDS_IN_A_DAY);
  }
}
